var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import AlertsSkeletonLoader from '../../../ui/molecules/AlertsSkeletonLoader';
import AccountsDropdown from '../AccountsDropdown';
import { useMarketingPrivacyQueryData, useProfileQueryData } from '../../../helpers/syfApiQueries';
import ContactInfoCard from '../ContactInfoCard';
import MarketingPrivacySettings from '../MarketingPrivacySettings';
import MarketingPrivacyInfoNotAvailable from '../MarketingPrivacyInfoNotAvailable';
import { SETPAY_PAYLATER_PRODUCT_TYPE, marketingPrivacySettingsCategories } from '../config';
import { usePageLoadStatus } from '../../../ui/customHooks';
import i18next from '../../../strings/i18n/i18n';
import alertsTriggerAnalyticsEvent, { analyticsHelper } from '../../../helpers/analyticsMiddleware';
import SyfStorage from '../../../helpers/SyfStorage';
import { MainPageWrapper, SkeletonLoader } from './subcomponents';
var defaultAddress = {
    'cipher.addressLine1': '',
    'cipher.addressLine2': '',
    'cipher.city': '',
    'cipher.state': '',
    'cipher.zipCode': '',
    'cipher.yearsAtAddress': ''
};
var MainPage = function (_a) {
    var _b;
    var platform = _a.platform, accountsInfo = _a.accountsInfo, handleProfileClick = _a.handleProfileClick, config = _a.config;
    var _c = useState((_b = accountsInfo[0]) === null || _b === void 0 ? void 0 : _b.accountId), accountId = _c[0], setAccountId = _c[1];
    var _d = useState({
        email: '',
        address: defaultAddress
    }), contactInfoData = _d[0], setContactInfoData = _d[1];
    var _e = useMarketingPrivacyQueryData(config, accountId), marketingSettingsData = _e.data, isMarketingPrivacyDataLoading = _e.isLoading, isMarketingPrivacyError = _e.isError;
    var _f = useProfileQueryData(config, accountId), data = _f.data, isProfileDataLoading = _f.isLoading, isProfileError = _f.isError;
    var preferenceUserData = accountsInfo === null || accountsInfo === void 0 ? void 0 : accountsInfo.find(function (account) { return (account === null || account === void 0 ? void 0 : account.accountId) === accountId; });
    var accountInfo = preferenceUserData.accountInfo, product = preferenceUserData.product;
    var isPageLoaded = usePageLoadStatus();
    var currentLanguage = "us|".concat(SyfStorage.getLanguage());
    var clientId = product === null || product === void 0 ? void 0 : product.clientId;
    var displayName = product === null || product === void 0 ? void 0 : product.displayName;
    var accountLast4 = accountInfo === null || accountInfo === void 0 ? void 0 : accountInfo.accountLast4;
    var productType = product === null || product === void 0 ? void 0 : product.type;
    var isPayLater = SETPAY_PAYLATER_PRODUCT_TYPE.includes(productType);
    var analyticsAccountInfoMessage = "".concat(displayName, " ending in ").concat(accountLast4);
    var isDataLoading = isMarketingPrivacyDataLoading || isProfileDataLoading;
    useEffect(function () {
        var _a, _b;
        if (!isProfileDataLoading && !isProfileError) {
            var email = ((_a = data === null || data === void 0 ? void 0 : data.profile) === null || _a === void 0 ? void 0 : _a['cipher.email']) || '';
            var address = ((_b = data === null || data === void 0 ? void 0 : data.profile) === null || _b === void 0 ? void 0 : _b.mailingAddress) || defaultAddress;
            setContactInfoData({ email: email, address: address });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isProfileDataLoading, isProfileError, accountId]);
    useEffect(function () {
        // only trigger pageload analytics for manage marketing preferences page
        if (isPageLoaded) {
            var _a = analyticsHelper.pageEvents.PREFERENCE_CENTER, PAGE_FUNCTION = _a.PAGE_FUNCTION, NAME = _a.NAME, PAGE_KIND = _a.PAGE_KIND;
            var siteIdentifier = analyticsHelper.siteIdentifier[platform];
            var pnl = analyticsHelper.pnl[platform];
            var defaultTriggerParams = {
                clientName: clientId,
                message: analyticsAccountInfoMessage,
                countryLanguage: currentLanguage,
                token: SyfStorage.getCorrelationId(),
                pageFunction: PAGE_FUNCTION,
                pageName: NAME,
                pageKind: PAGE_KIND,
                previouslyVisited: '',
                siteIdentifier: siteIdentifier,
                pnl: pnl,
                product: productType
            };
            // trigger analytics on page load for selected account
            if (isMarketingPrivacyError) {
                var errorMessage = "".concat(i18next.t('marketingPrivacySettingsError.infoNotAvailable'));
                alertsTriggerAnalyticsEvent(__assign(__assign({}, defaultTriggerParams), { errorMessage: errorMessage }));
            }
            else {
                alertsTriggerAnalyticsEvent(defaultTriggerParams);
            }
        }
    }, [
        currentLanguage,
        analyticsAccountInfoMessage,
        isPageLoaded,
        platform,
        productType,
        isMarketingPrivacyError,
        clientId
    ]);
    var marketingPrivacyInfo = isMarketingPrivacyError ? (_jsx(MarketingPrivacyInfoNotAvailable, { mfeConfig: config })) : (_jsx(MarketingPrivacySettings, { marketingSettingsData: marketingSettingsData === null || marketingSettingsData === void 0 ? void 0 : marketingSettingsData.marketingPreferences, accountId: accountId, config: config }));
    return (_jsxs(MainPageWrapper, __assign({ "data-test": "MainPageWrapper" }, { children: [accountsInfo.length > 1 && (_jsx(AccountsDropdown, { accountId: accountId, accountsInfo: accountsInfo, setAccountId: setAccountId })), isDataLoading ? (_jsx(_Fragment, { children: Object.keys(marketingPrivacySettingsCategories).map(function (settingsData) { return (_jsx(SkeletonLoader, { children: _jsx(AlertsSkeletonLoader, {}) }, settingsData)); }) })) : (_jsxs(_Fragment, { children: [_jsx(ContactInfoCard, { platform: platform, contactInfoData: contactInfoData, guid: accountId, handleProfileClick: handleProfileClick, isPayLater: isPayLater }), marketingPrivacyInfo] }))] })));
};
export default MainPage;
